import React, { useState, useEffect } from "react";
import axios from "../../config/adminAxios";
import {
  Box,
  DataTable,
  Text,
  Button,
  Layer,
  Heading,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
  Table,
  Spinner,
} from "grommet";
import API_URL from "../../config/config";
import { useNavigate } from "react-router-dom";
const UserAssessmentDetails = ({ user_id }) => {
  const [assessmentData, setAssessmentData] = useState([]);
  const [skillsData, setSkillsData] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const [showRecommendationModal, setShowRecommendationModal] = useState(false);
  const [selectedRecommendations, setSelectedRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  useEffect(() => {
    const load = async () => {
      try {
        const [summary, skills] = await Promise.all([
          axios.get(`${API_URL}/dashboard/assessmentSummary/${user_id}`),
          axios.get(`${API_URL}/user/getRolesAndSkills`),
        ]);
        setAssessmentData(summary.data.assessmentSummary ?? []);
        setSkillsData(skills.data.sub_product_skills ?? []);
      } catch (err) {
        console.error("Error loading assessment history:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user_id]);

  // Helper to find skill name by topic id
  const getTopicName = (topicId) => {
    const skill = skillsData.find((skill) => skill.id === topicId);
    return skill ? `${skill.product_name} - ${skill.skill_name}` : null;
  };

  const openModalWithTopics = (topics) => {
    setSelectedTopics(topics);
    setShowModal(true);
  };

  const openModalWithQuestions = (questions) => {
    setSelectedQuestions(questions);
    setShowQuestionsModal(true);
  };
  const openModalWithRecommendations = (assesment_id) => {
    setSelectedRecommendations(assesment_id);
    setShowRecommendationModal(true);
  };

  const closeModal = () => setShowModal(false);
  const closeQuestionsModal = () => setShowQuestionsModal(false);

  const getDuration = (startDate, startTime, endDate, endTime) => {
    if (!startDate || !startTime || !endDate || !endTime) {
      return "N/A";
    }

    const startDateTime = new Date(`${startDate.split("T")[0]}T${startTime}`);
    const endDateTime = new Date(`${endDate.split("T")[0]}T${endTime}`);

    if (isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime())) {
      return "N/A";
    }

    const durationMs = endDateTime - startDateTime;
    const minutes = Math.floor(durationMs / (1000 * 60));
    if (minutes < 1) return `1m`;
    return `${minutes}m`;
  };
  useEffect(() => {
    if (showRecommendationModal) {
      // Navigate to the new route using user_id and assessment_id
      navigate(`/assessment-quiz-result/${user_id}/${selectedRecommendations}`);
    }
  }, [showRecommendationModal, user_id, selectedRecommendations, navigate]);
  if (loading) {
    return (
      <Box pad="medium" direction="column" align="center" alignSelf="center">
        <Spinner />
        <Text>Loading...</Text>
      </Box>
    );
  }

  if (assessmentData.length === 0 || skillsData.length === 0)
    return (
      <Box pad="medium">
        <Text>Please take an assessment to view history</Text>
      </Box>
    );

  const columns = [
    {
      property: "topics",
      header: <Text>Skill(s) assessed on:</Text>,
      render: (datum) => {
        const topics = [
          getTopicName(datum.topic1),
          getTopicName(datum.topic2),
          getTopicName(datum.topic3),
          getTopicName(datum.topic4),
          getTopicName(datum.topic5),
          getTopicName(datum.topic6),
        ].filter(Boolean);

        return (
          <Box>
            {topics.map((topic, index) => (
              <Text key={index}>{topic}</Text>
            ))}
          </Box>
        );
      },
    },

    {
      property: "start_date",
      header: <Text>#</Text>,
      render: (datum) => (
        <Text>
          {new Date(datum.start_date).toLocaleDateString()} - {datum.start_time}
        </Text>
      ),
    },
    {
      property: "query_count",
      header: <Text>Number of Questions</Text>,
      render: (datum) => <Text>{datum.query_count}</Text>,
    },
    {
      property: "duration",
      header: <Text>Duration</Text>,
      render: (datum) => (
        <Text>
          {getDuration(
            datum.start_date,
            datum.start_time,
            datum.end_date,
            datum.end_time,
          )}
        </Text>
      ),
    },

    {
      property: "view_questions",
      header: <Text>View Questions</Text>,
      render: (datum) => (
        <Button
          label="View Questions"
          onClick={() => openModalWithQuestions(datum.questions)}
        />
      ),
    },
    {
      property: "view_recommendations",
      header: <Text>View Recommendations</Text>,
      render: (datum) => (
        <Button
          color={"brand"}
          label="View Course Recommendations"
          onClick={() => openModalWithRecommendations(datum.assesment_id)}
        />
      ),
    },
  ];

  return (
    <Box pad="medium" overflow="auto">
      {/* <Heading level="2">Assessment History</Heading> */}
      <Text weight="bold">View all your assessment details here</Text>
      <DataTable
        columns={columns}
        data={assessmentData}
        step={10} // For pagination
        sortable
        size="medium"
      />
      {showModal && (
        <Layer onEsc={closeModal} onClickOutside={closeModal}>
          <Box
            pad="medium"
            width="large"
            margin="medium"
            align="center"
            alignSelf="center"
            alignContent="center"
            overflow="auto" // Enable scrolling
            style={{ maxHeight: "80vh" }} // Limit the height of the modal
          >
            <Text weight="bold">Topics for this assessment</Text>
            <Table align="center">
              <TableHeader>
                <TableRow>
                  <TableCell scope="col" border="bottom">
                    <Text weight="bold">Topic</Text>
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectedTopics.map((topic, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Text>{topic}</Text>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Box align="center" pad="medium">
              <Button label="Close" onClick={closeModal} />
            </Box>
          </Box>
        </Layer>
      )}

      {showQuestionsModal && (
        <Layer onEsc={closeQuestionsModal} onClickOutside={closeQuestionsModal}>
          <Box
            pad="medium"
            width="large"
            margin="medium"
            align="center"
            alignSelf="center"
            alignContent="center"
            overflow="auto" // Enable scrolling
            style={{ maxHeight: "80vh" }} // Limit the height of the modal
          >
            <Heading level="3" margin={{ bottom: "small" }}>
              Questions for this assessment
            </Heading>
            <Table align="center">
              <TableHeader>
                <TableRow>
                  <TableCell scope="col" border="bottom">
                    <Text weight="bold">Question</Text>
                  </TableCell>
                  <TableCell scope="col" border="bottom">
                    <Text weight="bold">Skill</Text>
                  </TableCell>
                  <TableCell scope="col" border="bottom">
                    <Text weight="bold">Your Answer</Text>
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectedQuestions.map((question, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Text>{question.question}</Text>
                    </TableCell>
                    <TableCell>
                      <Text>{question.skill}</Text>
                    </TableCell>
                    <TableCell>
                      <Text weight="bold">{question.correct}</Text>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Box align="center" pad="medium">
              <Button label="Close" onClick={closeQuestionsModal} />
            </Box>
          </Box>
        </Layer>
      )}
    </Box>
  );
};

export default UserAssessmentDetails;
